import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Compass,
  Users,
  ChartLineUp,
  Megaphone,
  GearSix,
  Sparkle,
  ArrowRight,
  Quotes,
  Handshake,
  Lightning,
  Target,
  Infinity as InfinityIcon,
  Path,
  CurrencyDollar,
} from "@phosphor-icons/react";
import { Reveal, MaskLine, EASE } from "../lib/motion";
import { Eyebrow, PrimaryButton, GhostLink } from "../components/ui-kit";

const SERVICES = [
  { icon: Compass, title: "Strategy", lines: ["Business direction", "Growth planning", "Decision frameworks"], accent: "teal" },
  { icon: Users, title: "Leadership", lines: ["Founder coaching", "Leadership development", "Team effectiveness"], accent: "gold" },
  { icon: ChartLineUp, title: "Sales & Growth", lines: ["Revenue strategy", "Sales systems", "Go-to-market execution"], accent: "teal" },
  { icon: Megaphone, title: "Marketing", lines: ["Brand positioning", "Demand generation", "Content strategy"], accent: "gold" },
  { icon: GearSix, title: "Operations", lines: ["Business processes", "KPIs & systems", "Operational excellence"], accent: "teal" },
  { icon: Sparkle, title: "AI Adoption", lines: ["AI across functions", "Better decisions", "Elevated experience"], accent: "gold" },
  { icon: CurrencyDollar, title: "Finance & Funding", lines: ["Fundraising readiness", "Financial modelling & clarity", "Investor communication"], accent: "teal" },
];

const REASONS = [
  { icon: Lightning, title: "Practical experience", text: "Two decades of building, not just advising." },
  { icon: Target, title: "Execution over presentations", text: "We roll up our sleeves and build with you." },
  { icon: Handshake, title: "Founder-first thinking", text: "Your vision leads every decision we make." },
  { icon: InfinityIcon, title: "Long-term partnership", text: "We stay for the journey, not the quarter." },
  { icon: GearSix, title: "Business systems mindset", text: "We turn heroics into repeatable systems." },
  { icon: Path, title: "Sustainable growth", text: "Growth that compounds and endures." },
];

const CLIENTS = [
  { name: "STEMx", logo: "/images/clients/stemx.png", tagline: "An innovation lab for schools, universities and industries." },
  { name: "SSMRV College", logo: "/images/clients/ssmrv.png", tagline: "Institutional partner — Entrepreneurship Skills Development Programme, Bengaluru." },
  { name: "Jessi Publications", logo: "/images/clients/jessi.webp", tagline: "Publishing and content partner." },
  { name: "PYOC", logo: "/images/clients/pyoc.png", tagline: "Introducing a new category — “Living Apparel.”" },
  { name: "Dyemension", logo: "/images/clients/dyemension.png", tagline: "Where fashion meets the future." },
  { name: "InTribe", logo: "/images/clients/intribe.png", tagline: "Building the future of Human Growth Intelligence — technology that empowers every individual to learn, grow and belong." },
  { name: "CollectIQ", logo: "/images/clients/collectiq.png", tagline: "PAII — Creating physical asset intelligence infrastructure.", dark: true },
];

const TESTIMONIALS = [
  {
    quote: "Founder+ has helped us bring greater structure, clarity and execution discipline to our growth journey. Their practical, founder-centric approach goes beyond consulting — they challenge our thinking, strengthen our business systems and work alongside us to prepare STEMx for scale.",
    name: "Narasimha Naidu",
    role: "CEO · STEMx Innovations Pvt. Ltd.",
  },
  {
    quote: "Founder+ brought much-needed strategic clarity to our marketing and operational processes. Their practical mentorship helped us think differently, structure better and move from ideas to execution with greater discipline.",
    name: "A. Kumar",
    role: "CEO · Dyemension",
  },
  {
    quote: "Founder+ has been a valuable strategic partner in helping Jessi Publications move from ideas to structured execution. Their involvement has brought clarity to our Pan-India expansion, sales and channel strategy, marketing priorities, and business processes. What we particularly value is the practical, hands-on approach — Founder+ doesn't just advise us; they work alongside the team to build systems, create accountability and drive execution. Their understanding of the education ecosystem and founder-led businesses has made the engagement highly relevant to our growth journey.",
    name: "Siddhartha J",
    role: "CEO · Jessi Publications · Madurai, Tamil Nadu",
  },
];

const STORIES = [
  {
    stage: "Founder-led → Team-led",
    quote: "The company finally runs without me in every room — and it runs better.",
    outcome: "From bottleneck to leadership layer",
    detail: "Defined ownership, delegated the ten most-repeated decisions.",
  },
  {
    stage: "Instinct → System",
    quote: "We stopped selling on gut feel and built an engine anyone on the team can run.",
    outcome: "Repeatable, coachable sales motion",
    detail: "A first sales playbook, clear stages, predictable pipeline.",
  },
  {
    stage: "Growth → Endurance",
    quote: "We grew fast before. This time we grew in a way that actually lasts.",
    outcome: "Sustainable, compounding growth",
    detail: "Unit economics, KPIs and systems built before scaling.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <div data-testid="home-page">
      {/* ===================== HERO ===================== */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden bg-cloud">
        <div className="mx-auto grid min-h-[100svh] max-w-[1320px] grid-cols-1 items-center gap-8 px-6 pt-28 pb-16 lg:grid-cols-[1.05fr_1fr] lg:px-10 lg:pt-24">
          {/* Left copy */}
          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            >
              <Eyebrow>The Company Building Partner</Eyebrow>
            </motion.div>

            <h1 className="mt-7 font-heading text-5xl font-light leading-[1.05] tracking-tight text-navy sm:text-6xl lg:text-[4.6rem]">
              <MaskLine delay={0.15}>Every founder</MaskLine>
              <MaskLine delay={0.28}>deserves a</MaskLine>
              <MaskLine delay={0.41} className="font-semibold text-gold">
                Company Building
              </MaskLine>
              <MaskLine delay={0.54} className="font-semibold text-gold">
                Partner.
              </MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.8 }}
              className="mt-8 max-w-lg text-lg leading-relaxed text-slate-600"
            >
              Building a company shouldn't be a lonely journey. Founder+ partners with
              ambitious founders to transform startups into scalable, enduring
              companies — through strategy, leadership, execution, systems, and
              sustainable growth.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <PrimaryButton to="/contact" testid="hero-primary-cta">
                Let's Build Together <ArrowRight size={17} weight="bold" />
              </PrimaryButton>
              <GhostLink to="/how-we-help" testid="hero-secondary-cta">
                Learn More
              </GhostLink>
            </motion.div>
          </div>

          {/* Right image */}
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.2 }}
            className="relative z-0 h-[52vh] overflow-hidden rounded-[28px] shadow-[0_30px_80px_rgba(11,31,58,0.22)] lg:h-[76vh]"
          >
            <motion.img
              src="/images/hero.png"
              alt="A founder standing on a mountain summit at sunrise"
              style={{ y: imgY, scale: imgScale }}
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
            <motion.div
              style={{ y: overlayY }}
              className="absolute bottom-8 right-8 max-w-[240px] text-right"
            >
              <Quotes size={34} weight="fill" className="ml-auto text-gold" />
              <p className="mt-2 font-heading text-2xl font-medium leading-snug text-white">
                You are <span className="text-gold">not alone.</span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===================== MARQUEE ===================== */}
      <section className="border-y border-slate-100 bg-white py-8">
        <Marquee speed={38} gradient={false} className="overflow-hidden">
          {["Strategy", "Leadership", "Sales & Growth", "Marketing", "Operations", "AI Adoption", "Systems", "Partnership"].map(
            (w, i) => (
              <span
                key={i}
                className="mx-10 font-heading text-4xl font-medium tracking-tight marquee-stroke sm:text-5xl"
              >
                {w}
                <span className="mx-10 text-gold">+</span>
              </span>
            )
          )}
        </Marquee>
      </section>

      {/* ===================== CLIENT LOGOS / VENTURES ===================== */}
      <section className="bg-white pt-20 pb-8 lg:pt-24" data-testid="clients-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-teal">
              Ventures &amp; Partners We've Built With
            </p>
            <h2 className="mx-auto mt-5 max-w-2xl font-heading text-3xl font-medium leading-tight tracking-tight text-navy sm:text-4xl">
              Companies shaped by hands-on building.
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CLIENTS.map((c, i) => (
              <Reveal
                key={c.name}
                delay={(i % 3) * 0.08}
                data-testid={`client-logo-${i}`}
                className="group flex flex-col items-center rounded-3xl border border-slate-100 bg-cloud/40 p-8 text-center transition-all duration-500 hover:-translate-y-1.5 hover:border-slate-200 hover:shadow-[0_24px_60px_rgba(11,31,58,0.08)]"
              >
                <div
                  className={`flex h-24 w-full items-center justify-center rounded-2xl px-6 ${
                    c.dark ? "bg-navy" : "bg-white"
                  }`}
                >
                  <img
                    src={c.logo}
                    alt={`${c.name} logo`}
                    className="max-h-16 w-auto max-w-[180px] object-contain"
                  />
                </div>
                <p className="mt-6 text-[15px] leading-relaxed text-slate-500">{c.tagline}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WHY FOUNDER+ (manifesto) ===================== */}
      <section className="bg-white py-28 lg:py-36" data-testid="why-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <div className="grid gap-16 lg:grid-cols-[0.8fr_1.2fr]">
            <Reveal>
              <Eyebrow>Why Founder+ Exists</Eyebrow>
              <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
                From building a startup to building an{" "}
                <span className="text-teal">organization.</span>
              </h2>
              <p className="mt-8 text-lg leading-relaxed text-slate-600">
                Every startup begins with an idea. Every company succeeds because of
                people, systems, leadership, and execution.
              </p>
              <p className="mt-5 text-lg leading-relaxed text-slate-600">
                Many founders are exceptional at building products and acquiring
                customers. But building a company requires a different mindset —
                Founder+ exists to help founders make that transition.
              </p>
            </Reveal>

            <div className="space-y-4">
              {[
                { n: "01", t: "It starts with an idea", d: "The spark that pulls a founder out of comfort and into the unknown." },
                { n: "02", t: "It grows through people", d: "Products win customers, but people and culture build the company." },
                { n: "03", t: "It scales through systems", d: "Repeatable systems turn founder heroics into durable execution." },
                { n: "04", t: "It endures through leadership", d: "Great companies outgrow the founders who started them." },
              ].map((c, i) => (
                <Reveal
                  key={c.n}
                  delay={i * 0.08}
                  className="group flex gap-8 rounded-2xl border border-slate-100 bg-cloud/60 p-8 transition-colors duration-300 hover:border-gold/40"
                >
                  <div className="font-heading text-2xl font-bold text-gold">{c.n}</div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-navy">{c.t}</h3>
                    <p className="mt-2 leading-relaxed text-slate-500">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== HOW WE HELP ===================== */}
      <section className="bg-cloud py-28 lg:py-36" data-testid="services-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <Eyebrow>How We Help</Eyebrow>
              <h2 className="mt-6 max-w-xl font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
                Seven ways we build alongside you.
              </h2>
            </div>
            <GhostLink to="/how-we-help" testid="services-explore-link">
              Explore all capabilities
            </GhostLink>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal
                  key={s.title}
                  delay={(i % 3) * 0.08}
                  className="group relative overflow-hidden rounded-3xl border border-slate-100 bg-white p-9 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(11,31,58,0.10)]"
                >
                  <div
                    className={`grid h-14 w-14 place-items-center rounded-2xl ${
                      s.accent === "gold" ? "bg-gold/12 text-gold" : "bg-teal/10 text-teal"
                    }`}
                  >
                    <Icon size={26} weight="duotone" />
                  </div>
                  <h3 className="mt-7 font-heading text-2xl font-semibold text-navy">
                    {s.title}
                  </h3>
                  <ul className="mt-5 space-y-2.5">
                    {s.lines.map((l) => (
                      <li key={l} className="flex items-center gap-3 text-[15px] text-slate-600">
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            s.accent === "gold" ? "bg-gold" : "bg-teal"
                          }`}
                        />
                        {l}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== WHY FOUNDERS CHOOSE US ===================== */}
      <section className="bg-white py-28 lg:py-36" data-testid="reasons-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>Why Founders Choose Us</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              A partner, not a presentation deck.
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-x-12 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {REASONS.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal
                  key={r.title}
                  delay={(i % 3) * 0.07}
                  className="group flex gap-5 border-b border-slate-100 py-8"
                >
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-navy group-hover:text-gold">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-navy">{r.title}</h3>
                    <p className="mt-1.5 leading-relaxed text-slate-500">{r.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== TESTIMONIALS ===================== */}
      <section className="bg-white py-28 lg:py-36" data-testid="testimonials-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>Testimonials</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              In the words of the founders we build with.
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal
                key={i}
                delay={(i % 3) * 0.1}
                data-testid={`testimonial-card-${i}`}
                className="flex flex-col rounded-3xl border border-slate-100 bg-cloud/50 p-9 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(11,31,58,0.08)]"
              >
                <Quotes size={34} weight="fill" className="text-gold/50" />
                <p className="mt-5 flex-1 font-heading text-lg font-light leading-snug text-navy">
                  {t.quote}
                </p>
                <div className="mt-8 flex items-center gap-4 border-t border-slate-100 pt-6">
                  <div className="grid h-11 w-11 place-items-center rounded-full bg-navy/5 text-navy/30">
                    <Users size={20} weight="duotone" />
                  </div>
                  <div>
                    <p className="font-heading text-sm font-semibold text-navy">{t.name}</p>
                    <p className="text-sm text-slate-400">{t.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WHAT PARTNERSHIP LOOKS LIKE ===================== */}
      <section className="relative overflow-hidden bg-navy py-28 lg:py-36" data-testid="stories-section">
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow dark>What Partnership Looks Like</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-white sm:text-5xl">
              The transitions we help founders navigate.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-white/60">
              Every partnership is different — but the turning points rhyme. Here's the
              shape of the journeys we build alongside founders.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {STORIES.map((s, i) => (
              <Reveal
                key={s.stage}
                delay={i * 0.1}
                className="group flex flex-col rounded-3xl border border-white/10 bg-white/[0.04] p-9 backdrop-blur-sm transition-colors duration-500 hover:border-gold/40"
              >
                <span className="text-xs font-bold uppercase tracking-[0.22em] text-gold">
                  {s.stage}
                </span>
                <Quotes size={30} weight="fill" className="mt-6 text-white/20" />
                <p className="mt-4 flex-1 font-heading text-xl font-light leading-snug text-white">
                  {s.quote}
                </p>
                <div className="mt-8 border-t border-white/10 pt-5">
                  <p className="text-sm font-semibold text-white">{s.outcome}</p>
                  <p className="mt-1 text-sm text-white/50">{s.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== A NOTE FROM NIRMALYA ===================== */}
      <section className="bg-cloud py-28 lg:py-36" data-testid="note-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <div className="grid items-center gap-14 lg:grid-cols-[0.85fr_1.15fr]">
            <Reveal className="relative">
              <div className="overflow-hidden rounded-[28px] shadow-[0_30px_80px_rgba(11,31,58,0.18)]">
                <img
                  src="/images/nirmalya2.png"
                  alt="Nirmalya Banerjee, Founder of Founder+"
                  className="aspect-[4/5] w-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-4 rounded-2xl bg-navy px-6 py-5 text-white shadow-xl sm:-right-6">
                <p className="font-heading text-lg font-semibold">Nirmalya Banerjee</p>
                <p className="text-sm text-gold">Founder & CEO, Founder+</p>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <Eyebrow>A Note From Nirmalya</Eyebrow>
              <Quotes size={44} weight="fill" className="mt-6 text-gold/40" />
              <blockquote className="mt-4 font-heading text-2xl font-light leading-snug tracking-tight text-navy sm:text-[2rem]">
                For over two decades, I've worked across business leadership, revenue
                growth, and company building. One lesson has remained constant.
              </blockquote>
              <p className="mt-6 text-lg leading-relaxed text-slate-600">
                Building a startup is difficult. Building a company is different.
                Founder+ was created because I believe every founder deserves more
                than advice — they deserve someone who helps them build.
              </p>
              <GhostLink to="/about-nirmalya" testid="note-link" className="mt-8">
                Meet Nirmalya
              </GhostLink>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== INSTITUTIONS TEASER ===================== */}
      <section className="bg-white py-24 lg:py-28" data-testid="home-institutions-section">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <Reveal className="max-w-3xl">
            <Eyebrow>Founder+ for Institutions</Eyebrow>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight tracking-tight text-navy sm:text-5xl">
              Preparing students to <span className="text-gold">Build. Work. And Lead.</span>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Entrepreneurship development. Corporate readiness. Startup mentorship.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[
              { tag: "BUILD", title: "360° Entrepreneurship Development" },
              { tag: "WORK", title: "Corporate Readiness Lab" },
              { tag: "VENTURE", title: "Startup Mentorship" },
            ].map((p, i) => (
              <Reveal
                key={p.tag}
                delay={i * 0.08}
                data-testid={`home-inst-${p.tag.toLowerCase()}`}
                className="rounded-3xl border border-slate-100 bg-cloud/50 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/40"
              >
                <span className="text-xs font-bold uppercase tracking-[0.24em] text-gold">{p.tag}</span>
                <h3 className="mt-4 font-heading text-xl font-semibold text-navy">{p.title}</h3>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10">
              <GhostLink to="/institutions" testid="home-inst-explore">
                Explore For Institutions
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== CTA BAND ===================== */}
      <section className="relative overflow-hidden bg-navy" data-testid="cta-band">
        <img
          src="/images/cta.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-[1320px] px-6 py-28 text-center lg:px-10 lg:py-36">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-heading text-5xl font-light leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl">
              You are <span className="font-semibold text-gold">not alone.</span>
            </h2>
            <p className="mx-auto mt-7 max-w-xl text-lg text-white/70">
              Let's build something meaningful together.
            </p>
            <div className="mt-10 flex justify-center">
              <Link
                to="/contact"
                data-testid="cta-band-button"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
              >
                Schedule a Conversation <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
